/* eslint-disable react/no-multi-comp */

import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import autobind from 'autobind-decorator';

import cancel from 'spa/images/icons/cancel-light.svg';
import cancelDark from 'spa/images/icons/clear.svg';
import cancelLight from 'spa/images/icons/close-white.svg';
import './Tooltip.less';

function isFixed(element) {
  while (element) {
    if (window.getComputedStyle(element).position === 'fixed') {
      return true;
    }
    element = element.parentElement;
  }
  return false;
}


class Tooltip extends React.Component {
  static propTypes = {
    align: PropTypes.oneOf(['left', 'center', 'right', 'side-left', 'top', 'top-right']).isRequired,
    children: PropTypes.any,
    clickOutsideToClose: PropTypes.bool,
    containerClassName: PropTypes.string,
    onClose: PropTypes.func,
    qaName: PropTypes.string,
    rightOffset: PropTypes.number,
    target: PropTypes.object,
    theme: PropTypes.oneOf(['dark', 'light']),
    topOffset: PropTypes.number,
  }

  UNSAFE_componentWillMount() {
    const {theme} = this.props;

    if (theme) {
      this.cancelButton = theme === 'dark' ? cancelDark : cancelLight;
    } else {
      this.cancelButton = cancel;
    }
  }

  componentDidMount() {
    if (this.props.clickOutsideToClose) {
      this.clickOutside = this.clickOutsideToClose.bind(this);

      if ('touchend' in document.documentElement) {
        document.addEventListener('touchend', this.clickOutside, true);
      } else if (document.addEventListener) {
        document.addEventListener('click', this.clickOutside, true);
      } else {
        document.attachEvent('click', this.clickOutside);
      }
    }
  }

  componentWillUnmount() {
    if (this.props.clickOutsideToClose) {
      if ('touchend' in document.documentElement) {
        document.removeEventListener('touchend', this.clickOutside, true);
      } else if (document.addEventListener) {
        document.removeEventListener('click', this.clickOutside, true);
      } else {
        document.detachEvent('click', this.clickOutside);
      }
    }
  }

  /*
  * Close the tooltip if clicked outside
  */
  clickOutsideToClose = (event) => {
    const domNode = ReactDOM.findDOMNode(this);

    if ((!domNode || !domNode.contains(event.target))) {
      this.props.onClose();
    }
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      containerClassName = '',
      onClose,
      qaName,
      align,
      children,
      theme,
      clickOutsideToClose,
      rightOffset,
      topOffset,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */

    const bottomTriangle = align === 'top' || align === 'top-right';

    return (
      <div
        {...rest}
        className={`Tooltip ${containerClassName} Tooltip--align-${align}`}
      >
        {!bottomTriangle && (
          <div className={`Tooltip__triangle Tooltip__triangle--${align}`} />
        )}
        {onClose ? (
          <div>
            <div className="Tooltip__content Tooltip__content--dismissable">
              {children}
              <div className="Tooltip__close-button">
                <img
                  className="Tooltip__close-icon"
                  data-qa={`${qaName || 'tooltip'}-close-button`}
                  src={this.cancelButton}
                  alt=""
                  onClick={onClose}
                />
              </div>
            </div>
            {bottomTriangle && (
              <div className={`Tooltip__triangle Tooltip__triangle--${align}`} />
            )}
          </div>
        ) : (
          <div className="Tooltip__content">
            {children}
          </div>
        )}

      </div>
    );
  }
}

class TooltipContainer extends React.Component {
  static propTypes = {
    align: PropTypes.oneOf(['left', 'center', 'right', 'side-left', 'top', 'top-right']),
    children: PropTypes.any,
    containerStyle: PropTypes.object,
    hide: PropTypes.bool,
    noExpandX: PropTypes.bool,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    qaName: PropTypes.string,
    rightOffset: PropTypes.number,
    topOffset: PropTypes.number,
    width: PropTypes.number,
    zIndex: PropTypes.number,
  };

  static defaultProps = {
    align: 'right',
    zIndex: 10,
  };

  componentDidMount() {
    if (!this.props.hide) {
      this.onShow();
      this.onResize();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.hide && !this.props.hide) {
      this.onHide();
    } else if (!nextProps.hide && this.props.hide) {
      this.onShow();
    }
  }

  componentDidUpdate() {
    if (!this.props.hide) {
      this.renderContent();
      this.onResize();
    }
  }

  componentWillUnmount() {
    if (!this.props.hide) {
      this.onHide();
    }
  }

  onShow() {
    this.containerElement = document.createElement('div');
    this.containerElement.setAttribute('class', 'Tooltip__wrapper-element');
    document.body.appendChild(this.containerElement);

    this.isFixed = isFixed(this.content);

    Object.assign(this.containerElement.style, {
      position: this.isFixed ? 'fixed' : 'absolute',
      zIndex: this.props.zIndex,
    });

    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', this.onResize);
    this.onResize();
    this.renderContent();
  }

  onHide() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onResize);
    ReactDOM.unmountComponentAtNode(this.containerElement);
    this.containerElement.parentNode.removeChild(this.containerElement);
  }

  onResize = () => {
    let windowRect = document.documentElement.getBoundingClientRect();
    const contentRect = this.content.getBoundingClientRect();

    if (this.isFixed) {
      windowRect = {
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        width: windowRect.width,
        height: windowRect.height,
      };
    }

    const tooltipTopOffset = this.props.topOffset || 0;
    const tooltipRightOffset = this.props.rightOffset || 0;

    Object.assign(this.containerElement.style, {
      top: `${contentRect.top + tooltipTopOffset + contentRect.height - windowRect.top}px`,
      right: `auto`,
      left: `auto`,
      width: this.props.width ? `${this.props.width}px` : 'auto',
      minWidth: this.props.width ? this.props.width : `${contentRect.width}px`,
      maxWidth: this.props.noExpandX ? `${contentRect.width}px` : 'none',
    });
    const offset = windowRect && windowRect.width <= 320 ? 5 : contentRect.left - windowRect.left -
      this.props.width / 2 + contentRect.width / 2;

    switch (this.props.align) {
    case 'right':
      Object.assign(this.containerElement.style, {
        right: `${windowRect.width - contentRect.right}px`,
      });
      break;
    case 'center':
      if (!this.props.width) {
        throw new Error('You need to specify the width when using align="center" in <Tooltip>');
      }
      // below 320px wide the popup would be partially hidden offscreen so we move it
      // over when necessary
      Object.assign(this.containerElement.style, {
        left: `${offset}px`,
        maxWidth: windowRect.width - offset * 2,
        minWidth: 0,
      });
      break;
    case 'left':
      Object.assign(this.containerElement.style, {
        left: `${contentRect.left - windowRect.left}px`,
      });
      break;
    case 'side-left':
      Object.assign(this.containerElement.style, {
        left: `${contentRect.left - this.props.width}px`,
      });
      break;
    case 'top':
      Object.assign(this.containerElement.style, {
        left: `${contentRect.left - windowRect.left}px`,
      });
      break;
    case 'top-right':
      Object.assign(this.containerElement.style, {
        right: `${windowRect.right + tooltipRightOffset - contentRect.right}px`,
      });
      break;
    default:
      Object.assign(this.containerElement.style, {
        left: contentRect.left - windowRect.left,
      });
      break;
    }
  };

  @autobind contentRef(content) { this.content = content; }

  render() {
    return (
      <div
        ref={this.contentRef}
        onClick={this.props.onClick}
        style={this.props.containerStyle}
      />
    );
  }

  renderContent() {
    /* eslint-disable no-unused-vars */
    const {
      hide,
      children,
      containerStyle,
      noExpandX,
      onClick,
      zIndex,
      onClose,
      width,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */

    ReactDOM.unstable_renderSubtreeIntoContainer(
      this,
      <Tooltip {...rest} onClose={onClose}>{children}</Tooltip>,
      this.containerElement,
    );
  }
}

export default TooltipContainer;



// WEBPACK FOOTER //
// ./spa/Components/Tooltip.jsx